import { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Pencil, Trash2 } from 'lucide-react';

interface VenueNameComboBoxProps {
  value: string;
  onChange: (value: string) => void;
  venueType?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function VenueNameComboBox({
  value,
  onChange,
  venueType,
  placeholder = 'Select or type venue name...',
  disabled = false,
}: VenueNameComboBoxProps) {
  const [venueNames, setVenueNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [useCustom, setUseCustom] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadVenueNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueType]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (showDropdown && !target.closest('.relative')) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  async function loadVenueNames() {
    try {
      setLoading(true);
      const queryParams = venueType ? `?venueType=${encodeURIComponent(venueType)}` : '';
      const response = await fetch(`/api/admin/machines/venue-names${queryParams}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setVenueNames(data);
        
        // If current value isn't in the list and isn't empty, switch to custom mode
        if (value && !data.includes(value)) {
          setUseCustom(true);
        }
      }
    } catch (error) {
      console.error('Failed to load venue names:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleRenameVenueName(oldName: string, newName: string) {
    if (!newName.trim() || newName === oldName) {
      setEditingName(null);
      return;
    }

    try {
      const response = await fetch('/api/admin/machines/venue-names/rename', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ oldName, newName: newName.trim(), venueType }),
      });

      if (response.ok) {
        await loadVenueNames();
        if (value === oldName) {
          onChange(newName.trim());
        }
        setEditingName(null);
      } else {
        alert('Failed to rename venue name');
      }
    } catch (error) {
      console.error('Failed to rename venue name:', error);
      alert('Failed to rename venue name');
    }
  }

  async function handleDeleteVenueName(name: string) {
    if (!window.confirm(`Delete "${name}"? This will remove the venue name from all machines using it.`)) {
      return;
    }

    try {
      const response = await fetch('/api/admin/machines/venue-names/delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ venueName: name, venueType }),
      });

      if (response.ok) {
        await loadVenueNames();
        if (value === name) {
          onChange('');
        }
      } else {
        alert('Failed to delete venue name');
      }
    } catch (error) {
      console.error('Failed to delete venue name:', error);
      alert('Failed to delete venue name');
    }
  }

  // If user wants to type custom or there are no existing venues
  if (useCustom || venueNames.length === 0) {
    return (
      <div className="space-y-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
        {venueNames.length > 0 && (
          <button
            type="button"
            onClick={() => setUseCustom(false)}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            ← Choose from existing venues
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative space-y-2">
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled || loading}
          className="w-full flex items-center justify-between px-3 py-2 text-sm border border-slate-200 rounded-md bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className={value ? '' : 'text-slate-400'}>
            {value || placeholder}
          </span>
          <svg className="h-4 w-4 opacity-50" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>

        {showDropdown && (
          <div className="absolute z-[2000] w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {venueNames.map((name) => (
              <div key={name} className="group">
                {editingName === name ? (
                  <div className="p-2 flex items-center gap-2">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameVenueName(name, editValue);
                        } else if (e.key === 'Escape') {
                          setEditingName(null);
                        }
                      }}
                      className="flex-1 text-sm"
                      autoFocus
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleRenameVenueName(name, editValue)}
                      className="px-2"
                    >
                      Save
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer">
                    <button
                      type="button"
                      onClick={() => {
                        onChange(name);
                        setShowDropdown(false);
                      }}
                      className="flex-1 text-left text-sm"
                    >
                      {name}
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingName(name);
                          setEditValue(name);
                        }}
                        className="p-1 hover:bg-slate-200 rounded"
                        title="Edit"
                      >
                        <Pencil className="h-3 w-3 text-slate-600" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteVenueName(name);
                        }}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setUseCustom(true);
                setShowDropdown(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50"
            >
              + Add new venue name...
            </button>
          </div>
        )}
      </div>
      {venueNames.length > 0 && (
        <button
          type="button"
          onClick={() => setUseCustom(true)}
          className="text-xs text-slate-500 hover:text-slate-700"
        >
          or type custom name
        </button>
      )}
    </div>
  );
}

